/* Shared layout for the legal centre (Terms / Privacy / Risk disclosure / AML policy).

Pages pass:
  title         — page title (e.g. "Terms of Service")
  description   — one-line description shown under the title
  lastUpdated   — date string ("30 April 2026")
  intro         — plain-English summary (ReactNode)
  sections      — Array<{ id, heading, body: ReactNode }>
  testid        — root data-testid
*/
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Clock,
  FileText,
  LifeBuoy,
  Lock,
  Printer,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";

export const LEGAL_DOCS = [
  {
    to: "/terms",
    label: "Terms of Service",
    short: "Terms",
    icon: FileText,
    blurb: "The agreement that governs your PaisaCX account and use of the platform.",
  },
  {
    to: "/privacy",
    label: "Privacy Policy",
    short: "Privacy",
    icon: Lock,
    blurb: "What personal data we collect, why we need it, and the rights you have over it.",
  },
  {
    to: "/risk-disclosure",
    label: "Risk Disclosure",
    short: "Risk",
    icon: AlertTriangle,
    blurb: "The market, technology and regulatory risks of trading virtual digital assets.",
  },
  {
    to: "/aml-policy",
    label: "AML & KYC Policy",
    short: "AML & KYC",
    icon: ShieldCheck,
    blurb: "How we verify customers and prevent money laundering under PMLA and FIU-IND rules.",
  },
];

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -65% 0px", threshold: 0 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ids]);
  return [active, setActive];
}

function useReadProgress(ref) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const done = Math.min(Math.max(-rect.top + 96, 0), Math.max(total, 1));
      setPct(Math.round((done / Math.max(total, 1)) * 100));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref]);
  return pct;
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 88, behavior: "smooth" });
  window.history.replaceState(null, "", `#${id}`);
}

export default function LegalLayout({ title, description, lastUpdated, intro, sections, testid }) {
  const { pathname, hash } = useLocation();
  const articleRef = useRef(null);
  const ids = useRef(sections.map((s) => s.id)).current;
  const [active, setActive] = useActiveSection(ids);
  const progress = useReadProgress(articleRef);
  const [minutes, setMinutes] = useState(null);

  const current = LEGAL_DOCS.find((d) => d.to === pathname) || LEGAL_DOCS[0];
  const DocIcon = current.icon;
  const related = LEGAL_DOCS.filter((d) => d.to !== current.to);

  useEffect(() => {
    const id = hash ? decodeURIComponent(hash.slice(1)) : "";
    if (id && document.getElementById(id)) {
      requestAnimationFrame(() => scrollToSection(id));
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    const words = (articleRef.current?.textContent || "").trim().split(/\s+/).length;
    setMinutes(Math.max(1, Math.round(words / 220)));
  }, [sections]);

  const onTocClick = (e, id) => {
    e.preventDefault();
    setActive(id);
    scrollToSection(id);
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={testid}>
      <Header />

      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(var(--primary)/0.16),transparent_60%)]" />
        <div className="cm-container relative pb-10 pt-12 sm:pt-16">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/" className="transition hover:text-foreground">Home</Link>
            <ChevronRight size={12} />
            <span>Legal</span>
            <ChevronRight size={12} />
            <span className="text-foreground">{current.label}</span>
          </nav>

          <div className="mt-6 flex items-start gap-4">
            <span className="brand-gradient hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl text-primary-foreground sm:flex">
              <DocIcon size={20} />
            </span>
            <div className="min-w-0">
              <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{title}</h1>
              {description ? (
                <p className="mt-3 max-w-2xl text-[15px] leading-7 text-muted-foreground">{description}</p>
              ) : null}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays size={14} className="text-primary" />
                  Last updated <span className="font-medium text-foreground">{lastUpdated}</span>
                </span>
                {minutes ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={14} className="text-primary" />
                    {minutes} min read
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1.5">
                  <FileText size={14} className="text-primary" />
                  {sections.length} sections
                </span>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-1.5 transition hover:text-foreground print:hidden"
                >
                  <Printer size={14} className="text-primary" />
                  Print
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 print:hidden" data-testid="legal-doc-nav">
            {LEGAL_DOCS.map((d) => {
              const Icon = d.icon;
              return (
                <NavLink
                  key={d.to}
                  to={d.to}
                  data-testid={`legal-doc-${d.short.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                      isActive
                        ? "border-primary/50 bg-primary/10 text-foreground"
                        : "border-border bg-surface/60 text-muted-foreground hover:border-primary/30 hover:text-foreground"
                    }`
                  }
                >
                  <Icon size={14} />
                  {d.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      </section>

      <div className="cm-container grid gap-10 py-10 lg:grid-cols-[250px_minmax(0,1fr)] lg:gap-14 lg:py-14">
        <aside className="hidden lg:block print:hidden">
          <div className="sticky top-24 space-y-5">
            <div className="rounded-2xl border border-border bg-surface/50 p-4">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">On this page</p>
                <span className="num text-[11px] text-muted-foreground">{progress}%</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface-2">
                <div className="h-full rounded-full bg-primary transition-[width] duration-200" style={{ width: `${progress}%` }} />
              </div>
              <ol className="mt-4 max-h-[calc(100vh-20rem)] space-y-0.5 overflow-y-auto pr-1">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={(e) => onTocClick(e, s.id)}
                      data-testid={`legal-toc-${s.id}`}
                      className={`flex gap-2 rounded-lg px-2.5 py-1.5 text-[13px] leading-5 transition ${
                        active === s.id
                          ? "bg-primary/10 font-medium text-foreground"
                          : "text-muted-foreground hover:bg-surface-2/60 hover:text-foreground"
                      }`}
                    >
                      <span className={`num shrink-0 ${active === s.id ? "text-primary" : "text-muted-foreground/60"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{s.heading}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-2xl border border-border bg-surface/50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <LifeBuoy size={15} className="text-primary" />
                Questions?
              </div>
              <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                Our team can clarify anything in this document.
              </p>
              <Link to="/contact" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                Contact support <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </aside>

        <div className="min-w-0">
          <label className="mb-6 block lg:hidden print:hidden">
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Jump to section</span>
            <select
              value={active}
              onChange={(e) => {
                setActive(e.target.value);
                scrollToSection(e.target.value);
              }}
              className="mt-2 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary"
            >
              {sections.map((s, i) => (
                <option key={s.id} value={s.id}>
                  {i + 1}. {s.heading}
                </option>
              ))}
            </select>
          </label>

          <article ref={articleRef} className="max-w-[820px]">
            {intro ? (
              <div className="panel-gradient mb-10 rounded-2xl border border-primary/25 p-5 sm:p-6" data-testid="legal-summary">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                  <Sparkles size={14} />
                  In plain English
                </div>
                <div className="mt-3 text-[15px] leading-7 text-foreground/90">{intro}</div>
              </div>
            ) : null}

            {sections.map((s, i) => (
              <section
                key={s.id}
                id={s.id}
                className="scroll-mt-24 border-t border-border py-8 first-of-type:border-t-0 first-of-type:pt-0"
                data-testid={`legal-section-${s.id}`}
              >
                <h2 className="flex items-baseline gap-3 text-xl font-semibold tracking-tight sm:text-2xl">
                  <span className="num shrink-0 text-sm font-semibold text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <span>{s.heading}</span>
                </h2>
                <div className="legal-prose mt-4 text-[15px] leading-[1.85] text-foreground/85">{s.body}</div>
              </section>
            ))}

            <p className="mt-6 rounded-xl border border-border bg-surface/40 p-4 text-xs leading-6 text-muted-foreground">
              This document is provided for information and should be read together with the other PaisaCX
              policies listed below. If any translation differs from this English version, the English version
              prevails.
            </p>
          </article>

          <div className="mt-12 print:hidden" data-testid="legal-related">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Other legal documents</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {related.map((d) => {
                const Icon = d.icon;
                return (
                  <Link
                    key={d.to}
                    to={d.to}
                    className="group rounded-2xl border border-border bg-surface/50 p-4 transition hover:-translate-y-0.5 hover:border-primary/40"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon size={16} />
                    </span>
                    <p className="mt-3 flex items-center gap-1 text-sm font-semibold">
                      {d.label}
                      <ArrowRight size={13} className="opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
                    </p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">{d.blurb}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <style>{`
        .legal-prose p { margin-bottom: 1em; }
        .legal-prose p:last-child { margin-bottom: 0; }
        .legal-prose ul { margin: 0 0 1em; padding-left: 0; list-style: none; }
        .legal-prose ul:last-child { margin-bottom: 0; }
        .legal-prose li { position: relative; padding-left: 1.4em; margin-bottom: 0.5em; }
        .legal-prose li::before {
          content: ""; position: absolute; left: 0.25em; top: 0.75em;
          width: 6px; height: 6px; border-radius: 9999px; background: oklch(var(--primary));
        }
        .legal-prose strong { color: oklch(var(--foreground)); font-weight: 600; }
        .legal-prose a { color: oklch(var(--primary)); font-weight: 600; }
        .legal-prose a:hover { text-decoration: underline; }
        @media print {
          [data-testid="site-header"], [data-testid="site-footer"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}
